import React, { Dispatch, SetStateAction, useRef, useState } from "react";
import { Box, Divider, useMediaQuery } from "@material-ui/core";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS, Document } from "@contentful/rich-text-types";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

import { breakpoints, color, fontSize, spacingDefaults } from "style/constants";
import { StyledGrid } from "components/simple";

const ButtonGroupStyle = {
  width: "180px",
  cursor: "pointer",
  alignItems: "flex-end",
  borderRadius: "20px",
  backgroundColor: color.WHITE,
  padding: `0.25rem ${spacingDefaults.xsmall}`,
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.2)"
};

enum LayoutType {
  oneColumn = "One-Column",
  twoColumn = "Two-Column",
  threeColumn = "Three-Column",
  fourColumn = "Four-Column"
}

enum MediaType {
  videoMp4 = "video/mp4",
  photoSvg = "photo/svg",
  photoJpg = "photo/jpg",
  photoPng = "photo/png",
  html = "text/html"
}

interface Props {
  data?: Document;
  setHasVideoEnded?: Dispatch<SetStateAction<boolean>>;
  setHasVideo?: Dispatch<SetStateAction<boolean>>;
  layoutType?: LayoutType;
  textColor?: color;
  textAlign?: string;
  customFontSize?: fontSize;
}

const RichText: React.FC<Props> = ({
  data,
  setHasVideoEnded,
  setHasVideo,
  layoutType,
  textColor,
  textAlign,
  customFontSize
}: Props) => {
  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);
  const [showTranscript, setShowTranscript] = useState<boolean>(false);
  const videoEl = useRef<HTMLVideoElement>(null);
  let supposedCurrentTime = 0;

  const options = () => {
    return {
      renderNode: {
        ...{
          [BLOCKS.HEADING_2]: (node: any, children: any) => (
            <Box
              component="h2"
              fontWeight="medium"
              fontSize={fontSize.xlarge}
              textAlign={textAlign ?? undefined}
              mb={5}
            >
              {children}
            </Box>
          ),
          [BLOCKS.HEADING_3]: (node: any, children: any) => (
            <Box
              component="h3"
              fontWeight="medium"
              fontSize={fontSize.large}
              textAlign={textAlign ?? undefined}
              mb={5}
            >
              {children}
            </Box>
          ),
          [BLOCKS.HEADING_4]: (node: any, children: any) => (
            <Box
              component="h4"
              fontWeight="medium"
              fontSize={fontSize.medium}
              textAlign={textAlign ?? undefined}
              mb={5}
            >
              {children}
            </Box>
          ),
          [BLOCKS.HEADING_5]: (node: any, children: any) => (
            <Box
              component="h5"
              fontWeight="medium"
              fontSize={fontSize.normal}
              textAlign={textAlign ?? undefined}
              mb={5}
            >
              {children}
            </Box>
          ),
          [BLOCKS.HEADING_6]: (node: any, children: any) => (
            <Box
              component="h6"
              fontWeight="medium"
              fontSize={fontSize.xsmall}
              textAlign={textAlign ?? undefined}
              mb={5}
            >
              {children}
            </Box>
          ),
          [BLOCKS.PARAGRAPH]: (node: any, children: any) => (
            <StyledGrid maxWidth="1100px" textAlign={textAlign ?? undefined}>
              <Box
                fontSize={customFontSize ?? fontSize.normal}
                whiteSpace="pre-line"
                color={textColor || color.BLACK}
              >
                {children}
              </Box>
            </StyledGrid>
          ),
          [BLOCKS.HR]: () => <Divider />,
          [BLOCKS.UL_LIST]: (node: any, children: any) => (
            <StyledGrid container>
              <ul
                style={{
                  textAlign: (textAlign as any) ?? "left",
                  color: textColor || color.BLACK
                }}
              >
                {children}
              </ul>
            </StyledGrid>
          ),
          [BLOCKS.OL_LIST]: (node: any, children: any) => (
            <ul
              style={{
                textAlign: (textAlign as any) ?? "left",
                paddingLeft: `${spacingDefaults.normal}`,
                color: textColor || color.BLACK
              }}
            >
              {children}
            </ul>
          ),
          [BLOCKS.LIST_ITEM]: (node: any, children: any) => (
            <li>
              <Box fontSize={fontSize.normal}>{children}</Box>
            </li>
          ),
          [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
            const { file } = node.data.target.fields;
            return (
              <StyledGrid container centerContent margin={`${spacingDefaults.normal} 0`}>
                {/* not supporting right now */}
                {/* {file?.contentType === MediaType.html && (
                  <ParseHtml html={file?.url} />
                )} */}
                {file?.contentType === MediaType.videoMp4 ? (
                  <>
                    {setHasVideo && setHasVideoEnded && (
                      <>
                        {setHasVideo(true)}
                        <video
                          ref={videoEl}
                          key={file?.url}
                          width="100%"
                          controls
                          style={{ maxWidth: "850px" }}
                          onEnded={() => setHasVideoEnded(true)}
                          onTimeUpdate={() => {
                            if (!videoEl?.current?.seeking) {
                              supposedCurrentTime = videoEl?.current?.currentTime || 0;
                            }
                          }}
                          onSeeking={(e: any) => {
                            if (videoEl?.current?.currentTime) {
                              const delta = videoEl?.current?.currentTime - supposedCurrentTime;
                              if (delta > 0.01) {
                                videoEl.current.currentTime = supposedCurrentTime;
                              }
                            }
                          }}
                        >
                          <source src={file?.url} type={"video/mp4"} />
                        </video>
                      </>
                    )}
                  </>
                ) : (
                  <img src={file?.url} width="100%" style={{ maxWidth: "850px" }} />
                )}
              </StyledGrid>
            );
          },
          [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
            const { thumbnailImage, transcript, video } = node.data.target.fields;
            const { file: thumbnailFile } = thumbnailImage.fields;
            const { file } = video.fields;
            return (
              <StyledGrid centerContent margin={`${spacingDefaults.normal} 0`}>
                <video
                  ref={videoEl}
                  key={file?.url}
                  width={mobile ? "100%" : "850px"}
                  height={mobile ? "100%" : "480px"}
                  controls
                  style={{
                    maxWidth: "850px",
                    maxHeight: "480px"
                  }}
                  poster={thumbnailFile?.url}
                >
                  <source src={file?.url} type={"video/mp4"} />
                </video>
                {transcript && (
                  <StyledGrid container centerContent margin={`${spacingDefaults.small} 0`}>
                    <StyledGrid
                      container
                      onClick={() => setShowTranscript(!showTranscript)}
                      style={ButtonGroupStyle}
                    >
                      {showTranscript ? (
                        <>
                          <Box fontSize={fontSize.normal}>Hide Transcript</Box>
                          <ExpandLessIcon />
                        </>
                      ) : (
                        <>
                          <Box fontSize={fontSize.normal}>Show Transcript</Box>
                          <ExpandMoreIcon />
                        </>
                      )}
                    </StyledGrid>
                    {showTranscript && (
                      <StyledGrid container margin={`${spacingDefaults.small} 0`}>
                        <RichText data={transcript} />
                      </StyledGrid>
                    )}
                  </StyledGrid>
                )}
              </StyledGrid>
            );
          }
        }
      }
    };
  };
  if (!data) return <></>;
  return <>{documentToReactComponents(data, options())}</>;
};
export default RichText;
